import { useCallback, useEffect, useMemo, useState } from 'react';

import type { FilteredProduct, FilteredProductByFormulation, FilteredProductByType } from '@/api';
import { isStockDecoration } from '@/api';
import type { Filters } from '@/shared-ui/ProductFilter/ProductFilter';
import { useAppStore } from '@/state-management';

import { usePrescriptionItemFeatureFlags } from './usePrescriptionItemFeatureFlags';

export type UseFilteredMedicationOptionsProps = {
  /**
   * Initial set of filters to start with. Example of this use, is when there are
   * pre-selected filters from the Prescription context
   */
  initialFilters?: Filters;
  /**
   * The filtered product payload to be used to calculate the medication options from
   */
  medicationDataSource: FilteredProductByFormulation;
  /**
   * By default `filteredMedicationOptions` only the top recommended options are displayed. Setting this
   * true will display all available options
   *
   */
  displayAllOptions?: boolean;
  /**
   * Currently prescribed medication. This is used to ensure that the `filteredMedicationOptions` will
   * not exclude the medication from selection
   */
  prescribedMedicationId?: number;

  /**
   * The initial medication when reissuing a prescription to retian the intial medication in the filteredMedicationOptions
   */
  initialReissueMedication?: FilteredProduct;
};

/**
 * Handles the filter change logic, calculates available options.
 * @param root0
 * @param root0.initialFilters
 * @param root0.medicationDataSource
 * @param root0.displayAllOptions
 * @param root0.prescribedMedicationId
 * @param root0.initialMedicationId
 * @param root0.initialReissueMedication
 */
export const useFilteredMedicationOptions = ({
  initialFilters,
  medicationDataSource,
  displayAllOptions,
  prescribedMedicationId,
  initialReissueMedication
}: UseFilteredMedicationOptionsProps) => {
  const { medicationPadItems: alreadyPrescribedMedicationPadItems } =
    useAppStore.use.prescriptionAssistant().prescriptionPad;
  const { recommendedMedicationsCount } = usePrescriptionItemFeatureFlags();
  const [selectedFilters, setSelectedFilters] = useState(initialFilters);
  const [medicationOptions, setMedicationOptions] = useState<FilteredProduct[]>([]);
  const [isReady, setIsReady] = useState(false);

  /**
   * main function to calculated the medication options based on the filters.
   */
  const calculateMedicationOptionsFromFilters = useCallback(
    (changedFilters: Filters) => {
      setIsReady(false);
      setSelectedFilters(changedFilters);

      // find available options based on the selected filters
      const optionsByFilters = Array.from(changedFilters.values()).reduce<
        FilteredProductByFormulation | FilteredProduct[] | FilteredProductByType
      >((filteredProductOptions, filterName) => {
        if (Array.isArray(filteredProductOptions)) {
          return filteredProductOptions;
        }
        return filteredProductOptions?.[filterName];
      }, medicationDataSource);

      // if it doesn't yield any medication options (not an array), set to empty
      if (!Array.isArray(optionsByFilters)) {
        if (medicationOptions.length > 0) {
          setMedicationOptions([]);
        }
        setIsReady(true);
        return;
      }

      setMedicationOptions(optionsByFilters);
      setIsReady(true);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [medicationDataSource, medicationOptions.length]
  );

  // evaluate options on init.
  useEffect(() => {
    if (!initialFilters) {
      return;
    }
    calculateMedicationOptionsFromFilters(initialFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFilters]);

  /**
   * Filter out prescribed medications from the available medication options,
   * so that user won't be able to select the same options in a prescription
   */
  const filteredMedicationOptions = useMemo(() => {
    if (medicationOptions.length === 0) {
      return [];
    }
    const alreadyPrescribedMedicationIds = alreadyPrescribedMedicationPadItems.map((item) =>
      Number(item?.medicationPadItem?.productId)
    );

    // Filter out all inactive and unavailable medications
    let displayedMedications = medicationOptions.filter(
      (med) => !(isStockDecoration(med.decoration) && (med.decoration?.isInactive || med.decoration?.isUnavailable))
    );

    if (!displayAllOptions) {
      displayedMedications = displayedMedications.slice(0, recommendedMedicationsCount);

      // If our prescribed medication is not in the top recommendations, add it
      if (prescribedMedicationId) {
        const prescribedMed = medicationOptions.find((med) => med.id === prescribedMedicationId);
        if (prescribedMed && !displayedMedications.some((med) => med.id === prescribedMedicationId)) {
          displayedMedications = [...displayedMedications, prescribedMed];
        }
      }
    }

    // If our initial reissue medication is out of service and is and is not in the top recommendations, add it
    if (initialReissueMedication && isStockDecoration(initialReissueMedication.decoration)) {
      if (!displayedMedications.some((med) => med.id === initialReissueMedication.id)) {
        displayedMedications = [...displayedMedications, initialReissueMedication];
      }
    }

    return displayedMedications.filter((medication) => {
      return medication.id === prescribedMedicationId || !alreadyPrescribedMedicationIds.includes(medication.id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alreadyPrescribedMedicationPadItems, prescribedMedicationId, medicationOptions, displayAllOptions]);

  // memoising the result to ensure no unnecessary re-renders when used as dependency
  const memoisedState = useMemo(() => {
    return {
      selectedFilters,
      /**
       * Available medications to select from based on the selected filters
       */
      medicationOptions
    };
  }, [selectedFilters, medicationOptions]);

  return {
    ...memoisedState,
    /**
     * All available medication options excluding the ones that have already been prescribed,
     * so that user won't be able to select the same options in a prescription
     */
    filteredMedicationOptions,
    calculateMedicationOptionsFromFilters,
    /**
     * This is true when the filter calculation is done,
     * and medication options have been calculated.
     */
    isReady
  };
};
